// Version: 2.14
// v2.14: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v2.13: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v2.12: The client's folder name no longer shows under its name.
// v2.11: Agreed names and the Website Analytics Report. Schedule types are now
//        IT and Cyber Security Report, Website Analytics Report and External
//        Cyber Security Assessment. Website Analytics Report schedules (moved
//        here from the Digital Hub) are created and edited through
//        /api/clients/<slug>/analytics/report-schedule, so they keep their
//        recipient list; they are weekly or monthly only. Each card names its
//        type in full and shows the recipients for analytics schedules.
// v2.10: Report Schedules tab now also manages External Assessment schedules.
//        Admins pick a type (Report or Assessment) when creating; schedules
//        are keyed per (client, kind) so a client can have one of each. Pause/
//        edit/delete pass ?kind. Clients only ever see/manage their report
//        schedule (assessments are admin-run).
// v2.8: Report schedules repurposed — domains field removed (the schedule
//       now drives generate-report-clearview.py, not a domain scan). Fixed
//       Pause/Resume calling a PATCH method the backend never registered
//       (only PUT /api/schedules/<slug>/pause exists) — this was a live
//       405 on every click, unrelated to the report work but found while
//       in this file.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch, useAuth } from './auth';

const DAYS_OF_WEEK = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const FREQUENCY_LABELS = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
const ANALYTICS_KIND = 'analytics_report';
const KIND_META = {
  report:           { label: 'IT and Cyber Security Report',       short: 'IT and Cyber Security Report',  hint: 'Generates the client\'s IT and Cyber Security Report and emails it to their contact.' },
  [ANALYTICS_KIND]: { label: 'Website Analytics Report',           short: 'Website Analytics Report',      hint: 'Emails the Website Analytics Report to the recipients below. Weekly covers the last 7 days, monthly the last 30.' },
  assessment:       { label: 'External Cyber Security Assessment', short: 'External Assessment',           hint: 'Runs an External Cyber Security Assessment on the client\'s domains and office connections.' },
};
const kindOf = (s) => s?.kind || 'report';

// ── Report schedule form ──────────────────────────────────────────────────────

function ScheduleForm({ initial, clientSlug, onSave, onCancel, isAdmin, clients }) {
  const [slug,        setSlug]        = useState(initial?.slug        || clientSlug || '');
  const [kind,        setKind]        = useState(initial?.kind        || 'report');
  const [frequency,   setFrequency]   = useState(initial?.frequency   || 'monthly');
  const [dayOfWeek,   setDayOfWeek]   = useState(initial?.day_of_week  ?? 0);
  const [dayOfMonth,  setDayOfMonth]  = useState(initial?.day_of_month ?? 1);
  const [timeOfDay,   setTimeOfDay]   = useState(initial?.time_of_day  || '07:00');
  const [recipients,  setRecipients]  = useState((initial?.recipients || []).join(', '));
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const isAnalytics = kind === ANALYTICS_KIND;

  // Analytics schedules are weekly or monthly only and default to the
  // client's contact email when first created.
  useEffect(() => {
    if (!isAnalytics || initial) return;
    if (frequency === 'daily') setFrequency('monthly');
    const c = clients.find(x => x.slug === slug);
    if (c && !recipients) setRecipients(c.meta?.contact_email || '');
  }, [isAnalytics, slug]); // eslint-disable-line

  const handleSubmit = async () => {
    setError(''); setSaving(true);
    try {
      if (isAnalytics) {
        const res = await authFetch(`/api/clients/${slug}/analytics/report-schedule`, {
          method: 'POST',
          body: JSON.stringify({ enabled: initial ? initial.enabled !== false : true, frequency,
            day_of_week: dayOfWeek, day_of_month: dayOfMonth, time_of_day: timeOfDay, recipients }),
        });
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || 'Failed to save');
        onSave({ message: initial ? 'Schedule updated' : 'Website Analytics Report schedule created',
                 next_run_local: d.schedule?.next_run_local });
        return;
      }
      const method = initial ? 'PUT' : 'POST';
      const url    = initial ? `/api/schedules/${slug}?kind=${initial.kind || 'report'}` : '/api/schedules';
      const body   = initial
        ? { frequency, day_of_week: dayOfWeek, day_of_month: dayOfMonth, time_of_day: timeOfDay }
        : { slug, kind, frequency, day_of_week: dayOfWeek, day_of_month: dayOfMonth, time_of_day: timeOfDay };
      const res = await authFetch(url, { method, body: JSON.stringify(body) });
      const d   = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to save');
      onSave(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {error && <div className="error-msg">{error}</div>}
      {isAdmin && !initial && (
        <div>
          <label>Client</label>
          <select value={slug} onChange={e => setSlug(e.target.value)}>
            <option value="">— Select client —</option>
            {clients.map(c => <option key={c.slug} value={c.slug}>{c.display_name}</option>)}
          </select>
        </div>
      )}
      {isAdmin && !initial && (
        <div>
          <label>Schedule type</label>
          <select value={kind} onChange={e => setKind(e.target.value)}>
            <option value="report">{KIND_META.report.label}</option>
            <option value={ANALYTICS_KIND}>{KIND_META[ANALYTICS_KIND].label}</option>
            <option value="assessment">{KIND_META.assessment.label}</option>
          </select>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>{KIND_META[kind].hint}</div>
        </div>
      )}
      <div>
        <label>Frequency</label>
        <select value={frequency} onChange={e => setFrequency(e.target.value)}>
          {!isAnalytics && <option value="daily">Daily</option>}
          <option value="weekly">Weekly{isAnalytics ? ' (last 7 days)' : ''}</option>
          <option value="monthly">Monthly{isAnalytics ? ' (last 30 days)' : ''}</option>
        </select>
      </div>
      {frequency === 'weekly' && (
        <div>
          <label>Day of week</label>
          <select value={dayOfWeek} onChange={e => setDayOfWeek(Number(e.target.value))}>
            {DAYS_OF_WEEK.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        </div>
      )}
      {frequency === 'monthly' && (
        <div>
          <label>Day of month</label>
          <select value={dayOfMonth} onChange={e => setDayOfMonth(Number(e.target.value))}>
            {Array.from({length:28}, (_,i) => i+1).map(d => (
              <option key={d} value={d}>{d}{d===1?'st':d===2?'nd':d===3?'rd':'th'}</option>
            ))}
          </select>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Capped at 28 to avoid month-end issues</div>
        </div>
      )}
      <div>
        <label>Time (UK time — GMT/BST)</label>
        <input type="time" value={timeOfDay} onChange={e => setTimeOfDay(e.target.value)} />
        <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>
          Times are in UK local time and automatically adjust for GMT/BST
        </div>
      </div>
      {isAnalytics && (
        <div>
          <label>Recipients</label>
          <input type="text" value={recipients} onChange={e => setRecipients(e.target.value)} placeholder="name@client.co.uk, other@client.co.uk" />
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Up to 10 addresses, separated by commas</div>
        </div>
      )}
      <div style={{display:'flex',gap:8,marginTop:4}}>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || !slug || !timeOfDay || (isAnalytics && !recipients.trim())}>
          {saving ? <span className="spinner" /> : initial ? 'Save Changes' : 'Create Schedule'}
        </button>
        <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function StatusBadge({ status, enabled }) {
  if (!enabled) return <span style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:20,padding:'2px 10px'}}>Paused</span>;
  const map = {
    ok:      { color:'var(--green)', label:'Active' },
    skipped: { color:'var(--amber)', label:'Skipped' },
    failed:  { color:'var(--red)',   label:'Failed'  },
    null:    { color:'var(--accent)',label:'Scheduled'},
  };
  const { color, label } = map[status] || map[null];
  return <span style={{fontSize:11,fontWeight:700,color,background:`${color}18`,border:`1px solid ${color}40`,borderRadius:20,padding:'2px 10px'}}>{label}</span>;
}

function NotificationList({ notifications }) {
  if (!notifications?.length) return null;
  const iconMap  = { fired:'●', skipped:'⚠', failed:'✗' };
  const colorMap = { fired:'var(--green)', skipped:'var(--amber)', failed:'var(--red)' };
  return (
    <div style={{marginTop:12,borderTop:'1px solid var(--border)',paddingTop:12}}>
      <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>Recent Activity</div>
      {notifications.slice(0,5).map((n,i) => (
        <div key={i} style={{display:'flex',gap:8,alignItems:'flex-start',marginBottom:6}}>
          <span style={{color:colorMap[n.type]||'var(--text-muted)',fontSize:12,marginTop:1,flexShrink:0}}>{iconMap[n.type]||'○'}</span>
          <div>
            <div style={{fontSize:12,color:'var(--text)'}}>{n.message}</div>
            <div style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>{n.ts?.slice(0,16).replace('T',' ')} UTC</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Report Schedules tab ──────────────────────────────────────────────────────

function ReportSchedulesTab({ isAdmin, slug }) {
  const { user }                      = useAuth();
  const [schedules,   setSchedules]   = useState([]);
  const [clients,     setClients]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [acting,      setActing]      = useState({});
  const [flash,       setFlash]       = useState(null);

  const showFlash = (msg, ok=true) => { setFlash({ msg, ok }); setTimeout(() => setFlash(null), 5000); };

  const fetchSchedules = useCallback(() => {
    authFetch('/api/schedules')
      .then(r => r.json())
      .then(d => { setSchedules(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchSchedules();
    const interval = setInterval(fetchSchedules, 30000);
    return () => clearInterval(interval);
  }, [fetchSchedules]);

  useEffect(() => {
    if (!isAdmin) return;
    authFetch('/api/clients')
      .then(r => r.json())
      .then(d => setClients(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [isAdmin]);

  const withActing = (key, fn) => async (...args) => {
    setActing(a => ({...a, [key]: true}));
    try { await fn(...args); } finally { setActing(a => ({...a, [key]: false})); }
  };

  const handlePause = withActing('pause', async (sch) => {
    const res = await authFetch(`/api/schedules/${sch.slug}/pause?kind=${sch.kind || 'report'}`, { method: 'PUT' });
    const d = await res.json();
    showFlash(d.message || d.error, res.ok);
    fetchSchedules();
  });

  const handleDelete = withActing('delete', async (sch) => {
    if (!window.confirm(`Delete the ${KIND_META[kindOf(sch)]?.label || 'report'} schedule for ${sch.client_name}?`)) return;
    const res = await authFetch(`/api/schedules/${sch.slug}?kind=${sch.kind || 'report'}`, { method: 'DELETE' });
    const d = await res.json();
    showFlash(d.message || d.error, res.ok);
    fetchSchedules();
  });

  const handleSave = (d) => {
    showFlash(d.message + (d.next_run_local ? ` — next run: ${d.next_run_local}` : ''));
    setShowForm(false); setEditing(null); fetchSchedules();
  };

  const effectiveSlug = !isAdmin ? user?.client_slug : slug;
  const visibleSchedules = effectiveSlug
    ? schedules.filter(s => s.slug === effectiveSlug && (isAdmin || (s.kind || 'report') === 'report'))
    : schedules;
  const mySchedule = (effectiveSlug && !isAdmin)
    ? schedules.find(s => s.slug === effectiveSlug && (s.kind || 'report') === 'report')
    : null;

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div style={{fontSize:13,color:'var(--text-muted)'}}>
          {isAdmin ? 'Scheduled IT and Cyber Security Reports, Website Analytics Reports and External Cyber Security Assessments for all clients' : 'Manage when your IT and Cyber Security Report is generated and sent'}
        </div>
        {(!showForm && !editing) && (
          isAdmin
            ? <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ New Schedule</button>
            : !mySchedule && <button className="btn btn-primary" onClick={() => setShowForm(true)}>Set Up Schedule</button>
        )}
      </div>

      {flash && <div className={flash.ok ? 'success-msg' : 'error-msg'} style={{marginBottom:16}}>{flash.msg}</div>}

      {showForm && !editing && (
        <div className="card" style={{marginBottom:20}}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:16}}>{isAdmin ? 'New Schedule' : 'Set Up Your Report Schedule'}</div>
          <ScheduleForm clientSlug={!isAdmin ? user?.client_slug : ''} isAdmin={isAdmin} clients={clients} onSave={handleSave} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {loading ? (
        <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>
      ) : visibleSchedules.length === 0 && !showForm ? (
        <div className="card" style={{padding:48,textAlign:'center',color:'var(--text-muted)'}}>
          <div style={{fontSize:32,marginBottom:12}}>◷</div>
          <div style={{fontWeight:600,marginBottom:6}}>No schedules set up</div>
          <div style={{fontSize:13,marginBottom:20}}>{isAdmin ? 'Create a schedule to send a report or run an assessment for any client automatically' : 'Set up a schedule to automatically generate and send your IT and Cyber Security Report'}</div>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>{isAdmin ? '+ New Schedule' : 'Set Up Schedule'}</button>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {visibleSchedules.map(s => (
            <div key={`${s.slug}:${s.kind||'report'}`} className="card" style={{padding:0,overflow:'hidden'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',flexWrap:'wrap',gap:12}}>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:700}}>{s.client_name?.replace(/_/g,' ')}</div>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,borderRadius:20,padding:'2px 10px',
                    color:kindOf(s)==='assessment'?'var(--accent)':kindOf(s)===ANALYTICS_KIND?'var(--green)':'var(--text-muted)',
                    background:kindOf(s)==='assessment'?'rgba(0,212,255,0.12)':kindOf(s)===ANALYTICS_KIND?'rgba(48,209,88,0.12)':'var(--surface2)',
                    border:'1px solid var(--border)'}}>{KIND_META[kindOf(s)]?.short || 'Report'}</span>
                  <StatusBadge status={s.last_status} enabled={s.enabled} />
                </div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  <button className="btn btn-secondary btn-sm" disabled={acting['pause']} onClick={() => handlePause(s)}>
                    {acting['pause'] ? <span className="spinner" /> : s.enabled ? 'Pause' : 'Resume'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(s); setShowForm(false); }}>Edit</button>
                  <button className="btn btn-danger btn-sm" disabled={acting['delete']} onClick={() => handleDelete(s)}>
                    {acting['delete'] ? <span className="spinner" /> : 'Delete'}
                  </button>
                </div>
              </div>
              {editing?.slug === s.slug && (editing?.kind||'report') === (s.kind||'report') && (
                <div style={{borderTop:'1px solid var(--border)',padding:'16px 20px',background:'var(--surface2)'}}>
                  <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Edit Schedule</div>
                  <ScheduleForm initial={editing} clientSlug={s.slug} isAdmin={isAdmin} clients={clients} onSave={handleSave} onCancel={() => setEditing(null)} />
                </div>
              )}
              <div style={{borderTop:'1px solid var(--border)',padding:'14px 20px',background:'var(--surface2)',display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12}}>
                <div>
                  <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:3}}>Frequency</div>
                  <div style={{fontSize:13,fontWeight:600}}>
                    {FREQUENCY_LABELS[s.frequency] || s.frequency}
                    {s.frequency === 'weekly'  && ` — ${DAYS_OF_WEEK[s.day_of_week]}`}
                    {s.frequency === 'monthly' && ` — ${s.day_of_month}${s.day_of_month===1?'st':s.day_of_month===2?'nd':s.day_of_month===3?'rd':'th'}`}
                  </div>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:3}}>Time (UK)</div>
                  <div style={{fontSize:13,fontWeight:600,fontFamily:'var(--font-mono)'}}>{s.time_of_day}</div>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:3}}>Next Run</div>
                  <div style={{fontSize:13,color:'var(--accent)',fontWeight:600,fontFamily:'var(--font-mono)'}}>{s.next_run_local || '—'}</div>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:3}}>Last Run</div>
                  <div style={{fontSize:13,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>{s.last_run_local || 'Never'}</div>
                </div>
                {kindOf(s) === ANALYTICS_KIND && (
                  <div style={{gridColumn:'1 / -1'}}>
                    <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:3}}>Recipients</div>
                    <div style={{fontSize:13,wordBreak:'break-word'}}>{(s.recipients || []).join(', ') || '—'}</div>
                  </div>
                )}
              </div>
              {s.notifications?.length > 0 && (
                <div style={{borderTop:'1px solid var(--border)',padding:'14px 20px'}}>
                  <NotificationList notifications={s.notifications} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{marginTop:24,fontSize:12,color:'var(--text-muted)'}}>
        All times are displayed in UK local time (GMT/BST). Schedules run automatically. To send a report or run an assessment once, use Reports and Assessments.
      </div>
    </div>
  );
}

// ── Scan schedules tab (admin only) ──────────────────────────────────────────

const SCAN_GROUPS = [
  {
    title: 'Cyber Security Hub Schedules',
    items: [
      { key: 'dns-saas-check.py',           label: 'Email Security' },
      { key: 'headers-check.py',            label: 'HTTP Headers' },
      { key: 'blacklist-check.py',          label: 'Email Blacklists' },
      { key: 'phishing-check.py',           label: 'Phishing Detection' },
      { key: 'dns-monitor-check.py',        label: 'DNS Monitoring' },
      { key: 'credexposure-check.py',       label: 'Exposed Files' },
      { key: 'bucket-check.py',             label: 'Cloud Storage' },
      { key: 'shodan-check.py',             label: 'Internet Exposure' },
      { key: 'censys-check.py',             label: 'Infrastructure Scan' },
      { key: 'abuseipdb-check.py',          label: 'IP Reputation' },
      { key: 'subdomain-takeover-check.py', label: 'Subdomain Takeover' },
      { key: 'dbexposure-check.py',         label: 'Database Exposure' },
      { key: 'wpscan-check.py',             label: 'WordPress' },
      { key: 'urlhaus-check.py',            label: 'Threat Feeds' },
      { key: 'portscan-check.py',           label: 'Port Scan' },
    ],
  },
  {
    title: 'Digital Hub Schedules',
    items: [
      { key: 'domain-expiry-check.py', label: 'Domain Registrations' },
      { key: 'certs-check.py',         label: 'Certificates' },
      { key: 'seo-geo-check.py',           label: 'SEO and GEO' },
      { key: 'search-strength-check.py',   label: 'Search Strength' },
      { key: 'cookies-check.py',           label: 'Cookie Compliance' },
    ],
  },
  {
    title: 'Device Hub Schedules',
    items: [
      { key: 'stgcore-action1', label: 'Action1 Sync' },
    ],
  },
];

function ScanSchedulesTab() {
  const [config,   setConfig]   = useState({});
  const [edits,    setEdits]    = useState({});
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState({});
  const [running,  setRunning]  = useState({});
  const [flash,    setFlash]    = useState(null);

  const showFlash = (msg, ok=true) => { setFlash({ msg, ok }); setTimeout(() => setFlash(null), 4000); };

  useEffect(() => {
    authFetch('/api/admin/scheduler/config')
      .then(r => r.json())
      .then(d => { setConfig(d); setEdits(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Normalise a config value to a full object
  const norm = (val) => {
    if (!val) return { time: '02:00', frequency: 'daily', day_of_week: 0, day_of_month: 1 };
    if (typeof val === 'string') return { time: val, frequency: 'daily', day_of_week: 0, day_of_month: 1 };
    return { time: '02:00', frequency: 'daily', day_of_week: 0, day_of_month: 1, ...val };
  };

  const setField = (key, field, value) => {
    setEdits(d => ({ ...d, [key]: { ...norm(d[key]), [field]: value } }));
  };

  const configsEqual = (a, b) => JSON.stringify(norm(a)) === JSON.stringify(norm(b));

  const save = async (key) => {
    setSaving(s => ({...s, [key]: true}));
    try {
      const res = await authFetch('/api/admin/scheduler/config', {
        method: 'POST',
        body: JSON.stringify({ [key]: norm(edits[key]) }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Save failed');
      setConfig(c => ({...c, [key]: norm(edits[key])}));
      showFlash('Saved — takes effect within 5 minutes');
    } catch (e) {
      showFlash(e.message, false);
    } finally {
      setSaving(s => ({...s, [key]: false}));
    }
  };

  const runScan = async (key, label) => {
    setRunning(r => ({...r, [key]: true}));
    try {
      const res = await authFetch('/api/admin/run-scan', {
        method: 'POST',
        body: JSON.stringify({ script: key }),
      });
      const d = await res.json();
      showFlash(d.message || d.error, res.ok);
    } catch (e) {
      showFlash(`Failed to trigger ${label}`, false);
    } finally {
      setTimeout(() => setRunning(r => ({...r, [key]: false})), 3000);
    }
  };

  const freqSummary = (cfg) => {
    const c = norm(cfg);
    if (c.frequency === 'weekly')  return `Weekly — ${DAYS_OF_WEEK[c.day_of_week]}`;
    if (c.frequency === 'monthly') return `Monthly — day ${c.day_of_month}`;
    return 'Daily';
  };

  if (loading) return <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>;

  return (
    <div>
      <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:24}}>
        Schedule and run times for all central security scans. Changes take effect within 5 minutes — no restart required. All times are UK local time (GMT/BST).
      </div>

      {flash && <div className={flash.ok ? 'success-msg' : 'error-msg'} style={{marginBottom:16}}>{flash.msg}</div>}

      {SCAN_GROUPS.map(group => (
        <div key={group.title} style={{marginBottom:32}}>
          <div style={{fontSize:12,fontWeight:700,letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--border)'}}>
            {group.title}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:0}}>
            {group.items.map(item => {
              const current = config[item.key];
              const edited  = edits[item.key];
              const ec      = norm(edited);
              const changed = !configsEqual(current, edited);
              return (
                <div key={item.key} style={{borderBottom:'1px solid var(--border)',padding:'12px 0'}}>
                  {/* Label row */}
                  <div style={{display:'flex',alignItems:'center',gap:12,marginBottom: 10}}>
                    <div style={{flex:1,fontSize:13,fontWeight:600}}>{item.label}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)'}}>
                      {freqSummary(current)} at {norm(current).time}
                    </div>
                    <button className="btn btn-secondary btn-sm"
                     
                     
                      disabled={running[item.key]}
                      onClick={() => runScan(item.key, item.label)}
                      title={`Run ${item.label} now for all clients`}
                    >
                      {running[item.key] ? <><span className="spinner" /> Running...</> : '▶ Run Now'}
                    </button>
                  </div>
                  {/* Controls row */}
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <select className="input-sm"
                      value={ec.frequency}
                      onChange={e => setField(item.key, 'frequency', e.target.value)}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    {ec.frequency === 'weekly' && (
                      <select className="input-sm"
                        value={ec.day_of_week}
                        onChange={e => setField(item.key, 'day_of_week', Number(e.target.value))}>
                        {DAYS_OF_WEEK.map((d, i) => <option key={i} value={i}>{d}</option>)}
                      </select>
                    )}
                    {ec.frequency === 'monthly' && (
                      <select className="input-sm"
                        value={ec.day_of_month}
                        onChange={e => setField(item.key, 'day_of_month', Number(e.target.value))}>
                        {Array.from({length:28}, (_,i) => i+1).map(d => (
                          <option key={d} value={d}>{d}{d===1?'st':d===2?'nd':d===3?'rd':'th'}</option>
                        ))}
                      </select>
                    )}
                    <input className="input-sm"
                      type="time"
                      value={ec.time}
                      onChange={e => setField(item.key, 'time', e.target.value)}
                      style={{ width:110 }}
                    />
                    <button className="btn btn-primary btn-sm" style={{ opacity: changed ? 1 : 0.4 }}
                     
                     
                      disabled={!changed || saving[item.key]}
                      onClick={() => save(item.key)}
                    >
                      {saving[item.key] ? <span className="spinner" /> : 'Save'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Schedules page ───────────────────────────────────────────────────────

const ADMIN_TABS = [
  { id: 'reports',  label: 'Report Schedules' },
  { id: 'scans',    label: 'Scan Schedules' },
];

export default function Schedules({ hideHeader = false, slug = null }) {
  const { user }          = useAuth();
  const isAdmin           = user?.role === 'admin';
  const [tab, setTab]     = useState('reports');

  return (
    <div>


      {!hideHeader && (
        <div className="page-header" style={{marginBottom: isAdmin ? 0 : 24}}>
          <div className="page-title">Schedules</div>
          <div className="page-subtitle">
            {isAdmin ? 'Manage automated schedules and scan times' : 'Manage when your IT and Cyber Security Report is generated and sent'}
          </div>
        </div>
      )}

      {isAdmin && (
        <div style={{display:'flex',gap:2,borderBottom:'1px solid var(--border)',marginBottom:24,marginTop: hideHeader ? 0 : 16}}>
          {ADMIN_TABS.map(t => (
            <button className="tab-btn" aria-selected={tab === t.id} key={t.id} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>
      )}

      {(!isAdmin || tab === 'reports') && <ReportSchedulesTab isAdmin={isAdmin} slug={slug} />}
      {isAdmin && tab === 'scans'    && <ScanSchedulesTab />}
    </div>
  );
}